import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Youtube, MessageCircle, Star, ExternalLink, Cookie } from "lucide-react";
import { useSiteContent } from "../contexts/SiteContentContext";
import { STUDIO_ADDRESS } from "../lib/services";
import { useCookieConsent } from "../contexts/CookieConsentContext";

const DAY_LABELS = { mon: "Po", tue: "Út", wed: "St", thu: "Čt", fri: "Pá", sat: "So", sun: "Ne" };
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function formatBusinessHours(hours) {
  if (!hours || Object.keys(hours).length === 0) return "Po-Ne: 8:00 - 20:00";
  const lines = [];
  let i = 0;
  while (i < DAY_ORDER.length) {
    const day = DAY_ORDER[i];
    const h = hours[day] || {};
    const key = h.closed ? "closed" : `${h.open}-${h.close}`;
    let j = i;
    while (j + 1 < DAY_ORDER.length) {
      const next = hours[DAY_ORDER[j + 1]] || {};
      const nextKey = next.closed ? "closed" : `${next.open}-${next.close}`;
      if (nextKey !== key) break;
      j++;
    }
    const label = i === j ? DAY_LABELS[day] : `${DAY_LABELS[day]}–${DAY_LABELS[DAY_ORDER[j]]}`;
    lines.push(h.closed ? `${label}: zavřeno` : `${label}: ${h.open}–${h.close}`);
    i = j + 1;
  }
  return lines.join("\n");
}

export default function Contact() {
  const { isAllowed, reopenSettings } = useCookieConsent();
  const { links, text } = useSiteContent();
  const SOCIALS = [
    { icon: Facebook, label: "Facebook", href: links.facebook },
    { icon: Instagram, label: "Instagram", href: links.instagram },
    { icon: Youtube, label: "YouTube", href: links.youtube },
  ];
  const SOCIAL_LINKS = links;
  const mapsAllowed = isAllowed("functional");
  const openingHoursText = formatBusinessHours(text.business_hours);

  return (
    <section id="kontakt" className="relative py-24 md:py-32 tone-contact overflow-hidden" data-testid="contact-section">
      <div className="lit-contact absolute inset-0 pointer-events-none" />
      <div className="relative max-w-[1180px] mx-auto px-[22px] md:px-8">
        <div className="max-w-[560px] mb-14">
          <span className="eyebrow text-akcent" data-testid="contact-eyebrow">Kontakt</span>
          <h2 className="t-h1 mt-4 text-krem-100">Pojďme proměnit vaše auto.</h2>
          <p className="t-body mt-4 text-tlum-dark">Máte dotaz nebo chcete rezervovat termín? Jsme tu pro vás.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mx-2 md:mx-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="rounded-2xl p-7 md:p-9 card-dark"
          >
            <h3 className="t-h3 text-krem-100 mb-6">Kontaktní údaje</h3>
            <ul className="space-y-5">
              {[
                { icon: Phone, label: "Telefon", value: "+420 604 447 377", href: "tel:+420604447377" },
                { icon: Mail, label: "Email", value: "apexdetailingprague@gmail.com", href: "mailto:apexdetailingprague@gmail.com" },
                { icon: MapPin, label: "Lokalita", value: STUDIO_ADDRESS.full, href: STUDIO_ADDRESS.mapsUrl, external: true, hint: "Zobrazit na Google Mapách" },
                { icon: Clock, label: "Otevírací doba", value: openingHoursText },
              ].map((item, i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-akcent" style={{ background: "rgba(201,166,107,0.16)" }}>
                    <item.icon className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1">
                    <div className="t-label text-tlum-dark">{item.label}</div>
                    {item.href ? (
                      <a
                        href={item.href}
                        {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-krem-100 font-semibold hover:text-akcent transition-colors whitespace-pre-line"
                        data-testid={item.external ? "contact-location-link" : undefined}
                      >
                        {item.value}
                        {item.hint && (
                          <span className="block text-xs font-normal text-akcent mt-0.5 inline-flex items-center gap-1">
                            {item.hint} <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                      </a>
                    ) : (
                      <div className="text-krem-100 font-semibold whitespace-pre-line">{item.value}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="rounded-2xl p-7 md:p-9 flex flex-col card-dark"
          >
            <h3 className="t-h3 text-krem-100 mb-6">Sledujte nás</h3>
            <p className="text-tlum-dark mb-6 t-body">Zůstaňte v kontaktu a sledujte naši práci na sociálních sítích.</p>
            <div className="flex flex-wrap gap-3 mb-4">
              {SOCIALS.map((s, i) =>
                s.href ? (
                  <a
                    key={i}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-accent !py-3 !px-4"
                    data-testid={`social-${s.label.toLowerCase()}`}
                  >
                    <s.icon className="w-4 h-4" /> {s.label}
                  </a>
                ) : (
                  <span
                    key={i}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-tlum-dark font-medium cursor-default text-sm"
                    style={{ border: "0.5px solid var(--linka-tmava)" }}
                    title="Odkaz brzy doplníme"
                    data-testid={`social-${s.label.toLowerCase()}-soon`}
                  >
                    <s.icon className="w-4 h-4" /> {s.label}
                    <span className="text-[10px] uppercase tracking-wider text-akcent/70 ml-1">brzy</span>
                  </span>
                )
              )}
              {SOCIAL_LINKS.googleReviews && (
                <a
                  href={SOCIAL_LINKS.googleReviews}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-accent-dark !py-3 !px-4"
                  data-testid="social-google-reviews"
                >
                  <Star className="w-4 h-4" /> Google recenze
                </a>
              )}
            </div>
            <div className="mt-auto rounded-xl p-5" style={{ background: "rgba(201,166,107,0.10)", border: "0.5px solid rgba(201,166,107,0.35)" }}>
              <div className="flex items-start gap-3 mb-4">
                <MessageCircle className="w-4.5 h-4.5 text-akcent mt-0.5" />
                <p className="text-sm text-krem-100/85"><strong className="text-akcent">Tip:</strong> Nejrychlejší odpověď získáte telefonicky nebo přes WhatsApp</p>
              </div>
              <div className="flex gap-2.5">
                <a href="tel:+420604447377" className="btn-accent flex-1 !justify-center whitespace-nowrap !px-2" data-testid="contact-call-btn">Zavolat nyní</a>
                <a
                  href="https://wa.me/420604447377"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline flex-1 !justify-center whitespace-nowrap !px-2"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-6 rounded-2xl overflow-hidden card-dark"
          data-testid="contact-map"
        >
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3" style={{ borderBottom: "0.5px solid var(--linka-tmava)" }}>
            <div className="flex items-center gap-3">
              <MapPin className="w-4.5 h-4.5 text-akcent" />
              <div>
                <div className="text-krem-100 font-semibold text-sm">Kde nás najdete</div>
                <div className="text-xs text-tlum-dark">{STUDIO_ADDRESS.full} — sem můžete vůz přistavit</div>
              </div>
            </div>
            <a
              href={STUDIO_ADDRESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-4 py-2 rounded-full text-akcent hover:bg-akcent hover:text-uhel-900 transition-colors inline-flex items-center gap-1.5"
              style={{ border: "0.5px solid rgba(201,166,107,0.5)" }}
              data-testid="contact-map-open-btn"
            >
              Otevřít v Google Mapách <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {mapsAllowed ? (
            <iframe
              title="Apex Detailing — Ježkova 627, Zeleneč"
              src={STUDIO_ADDRESS.mapsEmbedUrl}
              className="w-full h-[300px] md:h-[380px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map-iframe"
            />
          ) : (
            <div
              className="w-full h-[300px] md:h-[380px] flex items-center justify-center p-6"
              style={{ borderTop: "0.5px solid var(--linka-tmava)" }}
              data-testid="contact-map-consent-placeholder"
            >
              <div className="max-w-md text-center">
                <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-3 text-akcent" style={{ background: "rgba(201,166,107,0.16)" }}>
                  <Cookie className="w-5 h-5" />
                </div>
                <p className="text-krem-100/80 text-sm">
                  Mapa Google se zobrazí, jakmile povolíte <strong className="text-akcent">funkční cookies</strong>. Google při načtení mapy nastavuje vlastní cookies.
                </p>
                <button
                  type="button"
                  onClick={reopenSettings}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-akcent text-xs font-semibold hover:bg-akcent hover:text-uhel-900 transition-colors"
                  style={{ border: "0.5px solid rgba(201,166,107,0.5)" }}
                  data-testid="contact-map-enable-cookies"
                >
                  Změnit nastavení cookies
                </button>
                <a
                  href={STUDIO_ADDRESS.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3 text-xs text-tlum-dark hover:text-akcent"
                >
                  …nebo otevřít přímo v Google Mapách →
                </a>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: false }}
          className="mt-6 rounded-xl px-6 py-5 text-tlum-dark text-sm flex flex-wrap items-center gap-3 justify-center text-center card-dark"
        >
          <span className="text-akcent font-semibold">Mobilní služba:</span>
          Přijedeme k vám domů nebo do práce. Pokrýváme okolí Zelenče do 25 km (výjimky po dohodě). Pracujeme bezoplachovou metodou — stačí přístup k elektřině do cca 10 metrů.
        </motion.div>
      </div>
    </section>
  );
}
